// 📁 src/components/Cart/CartPage.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import OrderSummaryPanel from '../Payment/OrderSummaryPanel';

const CartPage = () => {
  const [cartItems, setCartItems] = useState([]);
  const [config, setConfig] = useState({});
  const [promo, setPromo] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [companyLogo, setCompanyLogo] = useState('');
  const [email, setEmail] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [purchaseHistory, setPurchaseHistory] = useState([]);
  const [popupConfig, setPopupConfig] = useState(null); // ✅ stylish balloon state
  const navigate = useNavigate();

  useEffect(() => {
    const hydrateAll = () => {
      const storedConfig = JSON.parse(localStorage.getItem('adminPricingConfig') || '{}');
      setConfig(storedConfig);

      const registry = JSON.parse(localStorage.getItem('accountRegistry') || '{}');
      const activeEmail =
        sessionStorage.getItem('isLoggedIn') === 'true' ? localStorage.getItem('email') : null;
      if (!activeEmail) return;

      setEmail(activeEmail);
      const account = registry[activeEmail] || {};
      const tenantKey = account.tenantId || activeEmail;
      setTenantId(tenantKey);

      setCompanyName(account.companyName || '');
      setCompanyLogo(account.companyLogo || '');

      const cartKey = `cartItems_${tenantKey}`;
      const promoKey = `activePromo_${tenantKey}`;
      const currencyKey = `currency_${tenantKey}`;
      const removedKey = `removedItems_${tenantKey}`;
      const selectedKey = `selectedModuleId_${tenantKey}`;

      if (!localStorage.getItem(cartKey) && localStorage.getItem(`cartItems_${activeEmail}`)) {
        localStorage.setItem(cartKey, localStorage.getItem(`cartItems_${activeEmail}`));
      }
      if (!localStorage.getItem(promoKey) && localStorage.getItem(`activePromo_${activeEmail}`)) {
        localStorage.setItem(promoKey, localStorage.getItem(`activePromo_${activeEmail}`));
      }
      if (!localStorage.getItem(currencyKey) && localStorage.getItem(`currency_${activeEmail}`)) {
        localStorage.setItem(currencyKey, localStorage.getItem(`currency_${activeEmail}`));
      }
      if (
        !localStorage.getItem(removedKey) &&
        localStorage.getItem(`removedItems_${activeEmail}`)
      ) {
        localStorage.setItem(removedKey, localStorage.getItem(`removedItems_${activeEmail}`));
      }
      if (
        !localStorage.getItem(selectedKey) &&
        localStorage.getItem(`selectedModuleId_${activeEmail}`)
      ) {
        localStorage.setItem(selectedKey, localStorage.getItem(`selectedModuleId_${activeEmail}`));
      }

      const scopedCurrency = localStorage.getItem(currencyKey);
      if (scopedCurrency === 'USD' || scopedCurrency === 'BDT') {
        setCurrency(scopedCurrency);
        console.log(`💾 Currency hydrated for ${tenantKey}: ${scopedCurrency}`);
      } else {
        console.warn(`⚠️ No valid currency found for ${tenantKey}. Defaulting to USD.`);
        setCurrency('USD');
      }

      let activePromo = JSON.parse(localStorage.getItem(promoKey) || null);
      if (!activePromo || !activePromo.discountRate) {
        console.warn(`⚠️ Scoped promo not found for ${tenantKey}. Trying global fallback.`);
        activePromo = JSON.parse(localStorage.getItem('activePromo') || null);
      }

      if (activePromo) {
        const now = new Date();
        const from = new Date(activePromo.validFrom);
        const to = new Date(activePromo.validTo);
        if (now >= from && now <= to && activePromo.discountRate > 0) {
          setPromo(activePromo);
          localStorage.setItem('promoApplied', 'true');
          localStorage.setItem('promoStartDate', new Date().toISOString());
          localStorage.setItem('promoDurationMonths', activePromo.benefitDurationMonths || '12');
          localStorage.setItem('promoName', activePromo.occasion || '');
          localStorage.setItem('promoDiscountRate', activePromo.discountRate || '0');
        }
      }

      const hydratedCart = JSON.parse(localStorage.getItem(cartKey) || '[]');
      setCartItems(hydratedCart);

      const auditLog = JSON.parse(localStorage.getItem(`paymentAuditLog_${activeEmail}`) || '[]');
      const paidHistory = auditLog.filter((entry) => entry.paymentStatus === 'paid');
      setPurchaseHistory(paidHistory);

      const updatedCart = hydratedCart.filter(
        (item) => !paidHistory.some((entry) => entry.moduleName === item.name)
      );
      if (updatedCart.length !== hydratedCart.length) {
        localStorage.setItem(cartKey, JSON.stringify(updatedCart));
        setCartItems(updatedCart);
      }
    };

    hydrateAll();
    window.addEventListener('storage', hydrateAll);
    const interval = setInterval(hydrateAll, 60000);

    return () => {
      window.removeEventListener('storage', hydrateAll);
      clearInterval(interval);
    };
  }, []);

  const syncCart = (updated) => {
    setCartItems(updated);
    const cartKey = `cartItems_${tenantId}`;
    localStorage.setItem(cartKey, JSON.stringify(updated));
  };

  const updateQuantity = (id, qty) => {
    const updated = cartItems.map((item) =>
      item.id === id ? { ...item, quantity: Math.max(1, qty) } : item
    );
    syncCart(updated);
  };

  const removeFromCart = (id) => {
    const updated = cartItems.filter((item) => item.id !== id);
    syncCart(updated);

    const removedKey = `removedItems_${tenantId}`;
    const removedItems = JSON.parse(localStorage.getItem(removedKey) || '[]');
    localStorage.setItem(removedKey, JSON.stringify([...removedItems, id]));
  };

  const billingCycle = localStorage.getItem('billingCycle') || config.billingCycle || 'monthly';
  const selectedModules = cartItems.map((item) => ({
    name: item.name,
    price: item.price * item.quantity
  }));

  // ✅ Guard for Continue Purchase
  const handleContinuePurchase = () => {
    if (!cartItems || cartItems.length === 0) {
      setPopupConfig({
        type: 'warning',
        message: 'Please select module first.',
        colors: 'from-yellow-500 via-orange-500 to-red-500'
      });
      return;
    }
    console.log('🧭 Navigating to /payment with companyName:', companyName);
    navigate('/payment', {
      state: {
        tenantId,
        companyName,
        method: 'card'
      }
    });
  };

  return (
    <div className="cart-page-container flex flex-col md:flex-row gap-8 p-6">
      {/* Stylish balloon for empty cart warning */}
      {popupConfig && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
          <div
            className={`bg-gradient-to-r ${popupConfig.colors} text-white p-6 rounded-2xl shadow-xl max-w-sm text-center animate-bounce-once`}
          >
            <h3 className="text-lg font-bold mb-2">⚠️ Alert</h3>
            <p className="text-sm font-medium">{popupConfig.message}</p>
            <button
              onClick={() => setPopupConfig(null)}
              className="mt-4 bg-white text-indigo-600 px-4 py-2 rounded-full font-semibold hover:bg-gray-100"
            >
              Got it
            </button>
          </div>
        </div>
      )}

      <div className="md:w-2/3">
        <div className="flex items-center gap-4 mb-4">
          {companyLogo && (
            <img
              src={`/assets/logos/${companyLogo}`}
              alt="Company Logo"
              className="h-10 w-10 object-contain rounded"
              onError={(e) => {
                console.warn('⚠️ Logo failed to load:', companyLogo);
                e.target.style.display = 'none';
              }}
            />
          )}
          <div>
            <h2 className="text-xl font-bold text-indigo-700">
              {companyName ? `${companyName} Cart` : 'My Cart'}
            </h2>
            {email && <p className="text-xs text-gray-500 italic">Email: {email}</p>}
            {tenantId && <p className="text-xs text-gray-500 italic">Tenant ID: {tenantId}</p>}
          </div>
        </div>

        {/* Cart Items */}
        {cartItems.length === 0 ? (
          <p className="text-gray-600">No items in cart.</p>
        ) : (
          <div className="space-y-6">
            {cartItems.map((item) => (
              <div key={item.id} className="flex items-center gap-4 border rounded p-4 shadow-sm">
                <img
                  src={item.image}
                  alt={item.name}
                  className="w-24 h-24 object-contain rounded"
                />
                <div className="flex-1">
                  <h4 className="text-lg font-semibold">{item.name}</h4>
                  <p className="text-sm text-gray-600">
                    Price: {currency === 'BDT' ? `৳${item.price}` : `$${item.price}`}
                  </p>

                  {promo && (
                    <p className="text-xs text-yellow-700 italic mt-1">
                      ✓ Includes {promo.discountRate}% {promo.occasion} discount for{' '}
                      {promo.benefitDurationMonths} months
                    </p>
                  )}

                  <div className="flex items-center gap-2 mt-2">
                    <button
                      onClick={() => updateQuantity(item.id, item.quantity - 1)}
                      className="px-2 py-1 bg-gray-200 rounded"
                    >
                      −
                    </button>
                    <span>{item.quantity}</span>
                    <button
                      onClick={() => updateQuantity(item.id, item.quantity + 1)}
                      className="px-2 py-1 bg-gray-200 rounded"
                    >
                      +
                    </button>
                  </div>
                  <button
                    onClick={() => removeFromCart(item.id)}
                    className="mt-2 text-sm text-red-600 hover:underline"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Purchase History Section */}
        <div className="mt-8 border-t pt-4">
          <h3 className="text-lg font-semibold text-gray-700 mb-2">Purchase History</h3>
          {purchaseHistory.length === 0 ? (
            <p className="text-sm text-gray-500 italic">No past purchases found.</p>
          ) : (
            purchaseHistory.map((entry, idx) => (
              <div key={idx} className="text-xs text-gray-600 mb-2">
                <p>Module: {entry.moduleName}</p>
                <p>
                  Fee: {entry.currency} {entry.amount}
                </p>
                <p>Payment Method: {entry.method}</p>
                <p>
                  Subscribed On:{' '}
                  {new Date(entry.purchaseDate).toLocaleString('en-BD', {
                    hour: 'numeric',
                    minute: 'numeric',
                    hour12: true,
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric'
                  })}
                </p>
                <hr className="my-2 border-gray-300" />
              </div>
            ))
          )}
        </div>
      </div>

      {/* Order Summary */}
      <div className="md:w-1/3">
        <OrderSummaryPanel
          selectedModules={selectedModules}
          billingCycle={billingCycle}
          currency={currency}
          discountRate={config.annualDiscountRate || 0}
          vatRate={config.vatRate}
          promoName={promo?.occasion || ''}
          promoDiscountRate={promo?.discountRate || 0}
          promoDurationMonths={promo?.benefitDurationMonths || 0}
          tenantId={tenantId}
        />

        <button
          onClick={handleContinuePurchase}
          className="mt-4 w-full bg-indigo-600 text-white py-2 rounded hover:bg-indigo-700"
        >
          Continue Purchase
        </button>
      </div>
    </div>
  );
};

export default CartPage;
